import React, { useEffect, useMemo, useRef, useState } from "react";
import L from "leaflet";
import {
  Circle,
  CircleMarker,
  MapContainer as LeafletMap,
  TileLayer,
  useMap,
} from "react-leaflet";
import { useTranslation } from "react-i18next";
import API_BASE, { getAnglersByRadius } from "../../../api";

const ANGLER_FISHING_METHOD_OPTIONS = [
  "methods.boat_spinning",
  "methods.bottom_fishing",
  "methods.dead_bait",
  "methods.drift_fishing",
  "methods.feeder",
  "methods.fly_fishing",
  "methods.float_fishing",
  "methods.jigging",
  "methods.kayak_fishing",
  "methods.live_bait",
  "methods.other",
  "methods.popping",
  "methods.spinning",
  "methods.surf_casting",
  "methods.trolling",
];

function humanizeMethodValue(value) {
  const normalized = typeof value === "string" ? value.trim() : "";
  if (!normalized) return "";

  const labelSource = normalized.includes(".")
    ? normalized.split(".").pop() || normalized
    : normalized;

  return labelSource
    .split(/[_-]/)
    .filter(Boolean)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(" ");
}

function FitRadiusBounds({ lat, lng, radiusMeters }) {
  const map = useMap();

  useEffect(() => {
    if (!map) return;
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
    if (!Number.isFinite(radiusMeters) || radiusMeters <= 0) return;

    let cancelled = false;

    const fitBounds = () => {
      if (cancelled) return;

      const center = L.latLng(lat, lng);
      const bounds = center.toBounds(radiusMeters);

      map.fitBounds(bounds, {
        padding: [24, 24],
        maxZoom: radiusMeters <= 10000 ? 12 : 10,
      });
    };

    if (map._loaded) {
      fitBounds();
    } else {
      map.whenReady(fitBounds);
    }

    return () => {
      cancelled = true;
    };
  }, [lat, lng, map, radiusMeters]);

  return null;
}

export default function AnglersRadiusPanel({ data, onOpenProfile }) {
  const { t } = useTranslation();
  const requestIdRef = useRef(0);
  const [radiusKm, setRadiusKm] = useState(25);
  const [appliedRadiusKm, setAppliedRadiusKm] = useState(25);
  const [filterWaterType, setFilterWaterType] = useState("any");
  const [filterWayOfFishing, setFilterWayOfFishing] = useState("any");
  const [filterFishingMethod, setFilterFishingMethod] = useState("any");
  const [results, setResults] = useState([]);
  const [resultCount, setResultCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [hasAppliedSearch, setHasAppliedSearch] = useState(false);

  const lat = Number(data?.lat);
  const lng = Number(data?.lng);
  const radiusMeters = radiusKm * 1000;
  const placeName = data?.placeName || t("weather_selected_location");
  const formatDistanceLabel = (distance) =>
    t("weather_anglers_results_distance", { distance });
  const fishingMethodOptions = useMemo(
    () =>
      [...ANGLER_FISHING_METHOD_OPTIONS].sort((left, right) =>
        t(left, { defaultValue: humanizeMethodValue(left) }).localeCompare(
          t(right, { defaultValue: humanizeMethodValue(right) })
        )
      ),
    [t]
  );

  useEffect(() => {
    requestIdRef.current += 1;
    setRadiusKm(25);
    setAppliedRadiusKm(25);
    setFilterWaterType("any");
    setFilterWayOfFishing("any");
    setFilterFishingMethod("any");
    setResults([]);
    setResultCount(0);
    setIsLoading(false);
    setErrorMessage("");
    setHasAppliedSearch(false);
  }, [lat, lng]);

  const resolveProfileImage = (profileImage) => {
    if (!profileImage) return null;
    if (/^https?:\/\//i.test(profileImage)) return profileImage;
    return `${API_BASE}${profileImage}`;
  };

  const handleApply = async () => {
    const token =
      typeof window !== "undefined" ? window.localStorage.getItem("token") : "";
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    setAppliedRadiusKm(radiusKm);
    setHasAppliedSearch(true);
    setIsLoading(true);
    setErrorMessage("");

    try {
      const data = await getAnglersByRadius(lat, lng, radiusKm, token, {
        waterType: filterWaterType,
        wayOfFishing: filterWayOfFishing,
        fishingMethod: filterFishingMethod,
      });
      if (requestIdRef.current !== requestId) {
        return;
      }
      const nextResults = Array.isArray(data?.results) ? data.results : [];
      setResults(nextResults);
      setResultCount(
        typeof data?.count === "number" ? data.count : nextResults.length
      );
    } catch (error) {
      if (requestIdRef.current !== requestId) {
        return;
      }
      console.error("Failed to load anglers by radius:", error);
      setResults([]);
      setResultCount(0);
      setErrorMessage(
        t("weather_anglers_results_error")
      );
    } finally {
      if (requestIdRef.current !== requestId) {
        return;
      }
      setIsLoading(false);
    }
  };

  const handleOpenProfile = (username) => {
    if (!username || !onOpenProfile) return;
    onOpenProfile(username);
  };

  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    return null;
  }

  return (
    <div className="anglers-radius-panel">
      <div className="anglers-radius-header">
        <h3>{t("weather_anglers")}</h3>
        <p>{t("weather_anglers_intro")}</p>
      </div>

      <div className="anglers-radius-map-meta">
        <strong>{placeName}</strong>
        <span>
          {t("common_lat")}: {lat.toFixed(3)}, {t("common_lng")}: {lng.toFixed(3)}
        </span>
      </div>

      <div className="anglers-radius-map-shell">
        <LeafletMap
          center={[lat, lng]}
          zoom={10}
          className="anglers-radius-map"
          attributionControl={false}
          zoomControl={false}
          dragging={false}
          doubleClickZoom={false}
          scrollWheelZoom={false}
          touchZoom={false}
          boxZoom={false}
          keyboard={false}
        >
          <TileLayer
            attribution="&copy; OpenStreetMap contributors"
            url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
          />
          <FitRadiusBounds lat={lat} lng={lng} radiusMeters={radiusMeters} />
          <Circle
            center={[lat, lng]}
            radius={radiusMeters}
            pathOptions={{
              color: "#1976d2",
              fillColor: "#4aa3ff",
              fillOpacity: 0.16,
              weight: 2,
            }}
          />
          <CircleMarker
            center={[lat, lng]}
            radius={6}
            pathOptions={{
              color: "#0f5d8c",
              fillColor: "#1fa4c5",
              fillOpacity: 1,
              weight: 2,
            }}
          />
        </LeafletMap>
      </div>

      <div className="anglers-radius-controls">
        <div className="anglers-radius-summary">
          <span>{t("weather_anglers_radius_label")}</span>
          <strong>{formatDistanceLabel(radiusKm)}</strong>
        </div>

        <input
          type="range"
          min="5"
          max="500"
          step="5"
          value={radiusKm}
          onChange={(event) => setRadiusKm(Number(event.target.value))}
          className="anglers-radius-slider"
        />

        <div className="anglers-radius-scale">
          <span>{formatDistanceLabel(5)}</span>
          <span>{formatDistanceLabel(500)}</span>
        </div>

        <div className="anglers-radius-filters">
          <div className="anglers-radius-filter-field">
            <label htmlFor="anglers-radius-water-type">
              {t("weather_anglers_filter_water_type", {
                defaultValue: "Preferred Water Type",
              })}
            </label>
            <select
              id="anglers-radius-water-type"
              value={filterWaterType}
              onChange={(event) => setFilterWaterType(event.target.value)}
            >
              <option value="any">
                {t("weather_anglers_filter_any", { defaultValue: "Any" })}
              </option>
              <option value="freshwater">
                {t("weather_anglers_filter_fresh_water", {
                  defaultValue: "Fresh water",
                })}
              </option>
              <option value="saltwater">
                {t("weather_anglers_filter_salt_water", {
                  defaultValue: "Salt water",
                })}
              </option>
            </select>
          </div>

          <div className="anglers-radius-filter-field">
            <label htmlFor="anglers-radius-way-of-fishing">
              {t("weather_anglers_filter_way_of_fishing", {
                defaultValue: "Preferred Way of Fishing",
              })}
            </label>
            <select
              id="anglers-radius-way-of-fishing"
              value={filterWayOfFishing}
              onChange={(event) => setFilterWayOfFishing(event.target.value)}
            >
              <option value="any">
                {t("weather_anglers_filter_any", { defaultValue: "Any" })}
              </option>
              <option value="boat">
                {t("fishing_way_boat", { defaultValue: "Boat" })}
              </option>
              <option value="shore">
                {t("fishing_way_shore", { defaultValue: "Shore" })}
              </option>
            </select>
          </div>

          <div className="anglers-radius-filter-field">
            <label htmlFor="anglers-radius-fishing-method">
              {t("weather_anglers_filter_method", {
                defaultValue: "Fishing Method",
              })}
            </label>
            <select
              id="anglers-radius-fishing-method"
              value={filterFishingMethod}
              onChange={(event) => setFilterFishingMethod(event.target.value)}
            >
              <option value="any">
                {t("weather_anglers_filter_any", { defaultValue: "Any" })}
              </option>
              {fishingMethodOptions.map((methodKey) => (
                <option key={methodKey} value={methodKey}>
                  {t(methodKey, {
                    defaultValue: humanizeMethodValue(methodKey),
                  })}
                </option>
              ))}
            </select>
          </div>
        </div>

        <button
          type="button"
          className="anglers-radius-apply-btn"
          onClick={handleApply}
          disabled={isLoading}
        >
          {isLoading
            ? t("weather_anglers_loading")
            : t("apply")}
        </button>
      </div>

      <div className="anglers-radius-results">
        <h4>{t("weather_anglers_results_title")}</h4>
        {!hasAppliedSearch && !isLoading && !errorMessage && (
          <p>{t("weather_anglers_results_placeholder")}</p>
        )}
        {isLoading && (
          <p>{t("weather_anglers_results_loading")}</p>
        )}
        {!isLoading && errorMessage && (
          <p className="anglers-radius-results-error">{errorMessage}</p>
        )}
        {!isLoading && !errorMessage && hasAppliedSearch && resultCount === 0 && (
          <p>{t("weather_anglers_results_empty")}</p>
        )}
        {!isLoading && !errorMessage && resultCount > 0 && (
          <>
            <p className="anglers-radius-results-count">
              {t("weather_anglers_results_count", { count: resultCount })}
            </p>
            <div className="anglers-radius-results-list">
              {results.map((angler) => {
                const profileImage = resolveProfileImage(angler.profileImage);
                const placeLabel = [angler.city, angler.country]
                  .filter(Boolean)
                  .join(", ");
                const usernameInitial = (angler.username || "?")
                  .trim()
                  .charAt(0)
                  .toUpperCase();

                return (
                  <button
                    key={angler.id}
                    type="button"
                    className="anglers-radius-result-item anglers-radius-result-button"
                    onClick={() => handleOpenProfile(angler.username)}
                  >
                    {profileImage ? (
                      <img
                        src={profileImage}
                        alt={angler.username}
                        className="anglers-radius-result-avatar"
                      />
                    ) : (
                      <div className="anglers-radius-result-avatar anglers-radius-result-avatar-fallback">
                        {usernameInitial}
                      </div>
                    )}
                    <div className="anglers-radius-result-body">
                      <strong>{angler.username}</strong>
                      {placeLabel && (
                        <span className="anglers-radius-result-location">
                          {placeLabel}
                        </span>
                      )}
                    </div>
                    <span className="anglers-radius-result-distance">
                      {formatDistanceLabel(angler.distanceKm)}
                    </span>
                  </button>
                );
              })}
            </div>
          </>
        )}
        <p className="anglers-radius-results-meta">
          {t("weather_anglers_results_radius", {
            radius: appliedRadiusKm,
            place: placeName,
          })}
        </p>
      </div>
    </div>
  );
}
